const React = require("react");
const { MdExpandMore, MdExpandLess, MdRefresh } = require("react-icons/md");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { Button } = require("./ui");
const { taxProductArea } = require("../../../src/includes/auth/apiProduct.js");
// Loaded on demand: a static require would put usa.json back in the main bundle.
const { findUsaZipArea } = require("../../../src/includes/pageInside/usaZipIndex.js");
const { calculateCJPricing, DEFAULT_TAX_RATE } = require("../cjPricing");
const {
  countryName, selectedVariantIds, variantSelectionPayload, quoteSignature, selectedVariantsCost,
  selectedOption, defaultCarrier, computeCountryPricing, buildShippingDestinationsPayload,
  initialStateFromProduct, resolveLegacySelection,
} = require("../cjShippingDestinations");
const { CJCountrySelector } = require("./cjShipping/CJCountrySelector");
const { CJVariantMultiSelector } = require("./cjShipping/CJVariantMultiSelector");
const { CJShippingCountryCard } = require("./cjShipping/CJShippingCountryCard");
const { CJShippingComparison } = require("./cjShipping/CJShippingComparison");
// ESM default export required from CJS (same interop as react-bootstrap in CJSyncModal.js).
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const SelectableOptionGrid = interopDefault(require("../../../src/includes/pageInside/SelectableOptionGrid"));
const SimulationTaxInput = interopDefault(require("../../../src/includes/pageInside/SimulationTaxInput"));
const h = React.createElement;

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

// Countries quoted at the same time. The server's CJ client is already
// limited to one CJ request per second process-wide; 2 in flight keeps one
// country "Calculating..." while the next one is queued, without ever
// firing an uncontrolled burst.
const QUOTE_CONCURRENCY = 2;

const QUOTE_ERROR_TEXT = {
  CJ_TIMEOUT: ["h_cjms_err_timeout", "CJ did not answer in time. Try again."],
  CJ_UNSUPPORTED_COUNTRY: ["h_cjms_err_unsupported", "CJ does not ship this product to {country}."],
  VARIANT_UNAVAILABLE: ["h_cjms_err_variant", "The selected variants are no longer available at CJ."],
  CJ_PRODUCT_UNAVAILABLE: ["h_cjms_err_product", "This CJ product is no longer available."],
  INVALID_QUANTITY: ["h_cjms_err_quantity", "Quantity must be between 1 and 1000."],
  CJ_RATE_LIMITED: ["h_cjms_err_rate", "CJ is limiting requests right now. Try again in a moment."],
  CJ_API_UNAVAILABLE: ["h_cjms_err_api", "CJ Dropshipping is unavailable right now."],
};

const runPool = async (items, worker, limit) => {
  let cursor = 0;
  const next = async () => {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      // eslint-disable-next-line no-await-in-loop
      await worker(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, next));
};

/**
 * "Shipping Cost Calculation" -- multi-country CJ shipping comparison and
 * per-country Sales Simulation, used by the CJ view modal tab and by step 2
 * of the Sync / Resync wizard (CJShippingPricingStep).
 *
 *  - ONE global variant selection (CJVariantMultiSelector): the same variants
 *    are quoted for every country -- never a per-country variant list;
 *  - 1 to 4 destination countries (CJCountrySelector), each quoted by its own
 *    request (independent loading, error, refresh);
 *  - per country: its own methods, selected method, cost, delivery time,
 *    optional selling price, profit and sellActive switch;
 *  - every fee/tax/profit figure comes from calculateCJPricing (cjPricing.js).
 *
 * Props: `autoCalculate` compares once the variants are loaded;
 * `shippingMode` ("include" | "separate") only changes what the customer is
 * billed for shipping (0 in "include"); `initialConfig` (a product's
 * dropshippingData) pre-fills countries / variants / prices on a Resync;
 * `onStateChange` / `onPricingChange` report to the enclosing wizard.
 */
const ShippingCostCalculator = ({
  productId, costPrice, t, autoCalculate = false, shippingMode = "separate",
  onStateChange, onPricingChange, initialConfig = null,
}) => {
  const { api } = useCJDropshipping();
  const initial = React.useMemo(() => initialStateFromProduct(initialConfig), []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Global inputs: countries, variants, quantity ---
  const [countries, setCountries] = React.useState(initial.countries);
  const [variants, setVariants] = React.useState([]);
  const [variantsLoading, setVariantsLoading] = React.useState(true);
  const [variantsError, setVariantsError] = React.useState("");
  const [selection, setSelection] = React.useState(initial.variantSelection);
  const [quantity, setQuantity] = React.useState(1);
  const quantityValid = Number.isInteger(Number(quantity)) && Number(quantity) >= 1 && Number(quantity) <= 1000;

  // --- Per-country state: { status, options, selectedCarrier, signature,
  //     calculatedAt, error, refreshError, sellingPriceOverride, sellActive }
  const [perCountry, setPerCountry] = React.useState(() => Object.fromEntries(initial.countries.map((code) => [code, {
    status: "idle",
    sellingPriceOverride: initial.overrides[code] || "",
    sellActive: initial.sellActive[code] !== false,
  }])));
  const patchCountry = (code, patch) => setPerCountry((prev) => ({ ...prev, [code]: { ...(prev[code] || { status: "idle" }), ...patch } }));

  const loadVariants = React.useCallback(async () => {
    setVariantsLoading(true);
    setVariantsError("");
    try {
      const data = await api.getProductVariants(productId);
      const list = Array.isArray(data?.variants) ? data.variants : [];
      setVariants(list);
      setSelection((current) => resolveLegacySelection(current, list));
    } catch (error) {
      setVariantsError(error.message || t("h_cjms_variants_error", "Could not load the variants."));
    } finally {
      setVariantsLoading(false);
    }
  }, [api, productId, t]);
  React.useEffect(() => { loadVariants(); }, [loadVariants]);

  const effectiveSelection = selection?.mode === "legacyExcluded" ? { mode: "all" } : selection;
  const selectedIds = selectedVariantIds(effectiveSelection, variants);
  const noVariantSelected = variants.length > 0 && selectedIds.length === 0;
  const signature = quoteSignature(effectiveSelection, variants, quantity);

  // ONE country, ONE request. A failed refresh never replaces a valid result.
  const quoteCountry = async (code, { force = false } = {}) => {
    const requestSignature = signature;
    patchCountry(code, { status: "loading", refreshError: "" });
    const payload = {
      endCountryCode: code,
      quantity: Number(quantity) || 1,
      refresh: force,
      ...(effectiveSelection?.mode === "all" || !variants.length
        ? { selectAllVariants: true }
        : { externalVariantIds: selectedIds }),
    };
    let data;
    try {
      data = await api.getProductShippingQuote(productId, payload);
    } catch (error) {
      const [key, fallback] = QUOTE_ERROR_TEXT[error.code] || [];
      const message = key ? t(key, fallback, { country: countryName(code) }) : (error.message || t("h_cjms_err_quote", "CJ could not quote this country."));
      setPerCountry((prev) => {
        const previous = prev[code] || {};
        if (previous.options?.length && previous.lastGoodStatus === "success") {
          return { ...prev, [code]: { ...previous, status: "success", refreshError: message } };
        }
        return { ...prev, [code]: { ...previous, status: "error", error: message, errorCode: error.code || "" } };
      });
      return;
    }
    setPerCountry((prev) => {
      const previous = prev[code] || {};
      const options = Array.isArray(data?.options) ? data.options : [];
      const keep = [previous.selectedCarrier, initial.carriers[code]].find((carrier) => carrier && options.some((o) => o.carrier === carrier));
      return {
        ...prev,
        [code]: {
          ...previous,
          status: "success",
          lastGoodStatus: "success",
          options,
          selectedCarrier: keep || defaultCarrier(options),
          signature: requestSignature,
          calculatedAt: data.calculatedAt || new Date().toISOString(),
          quotedVariantIds: data.quotedVariantIds || [],
          missingVariantIds: data.missingVariantIds || [],
          error: "",
          refreshError: "",
        },
      };
    });
  };

  const [comparing, setComparing] = React.useState(false);
  const compareAll = async ({ force = false } = {}) => {
    if (!quantityValid || noVariantSelected) return;
    setComparing(true);
    const codes = [...countries];
    setPerCountry((prev) => ({ ...prev, ...Object.fromEntries(codes.map((code) => [code, { ...(prev[code] || {}), status: "waiting" }])) }));
    await runPool(codes, (code) => quoteCountry(code, { force }), QUOTE_CONCURRENCY);
    setComparing(false);
  };

  const autoDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoCalculate || autoDoneRef.current || variantsLoading) return;
    autoDoneRef.current = true;
    compareAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCalculate, variantsLoading]);

  const handleCountriesChange = (next) => {
    setCountries(next);
    setPerCountry((prev) => {
      const copy = { ...prev };
      next.forEach((code) => {
        if (!copy[code]) copy[code] = { status: "idle", sellingPriceOverride: initial.overrides[code] || "", sellActive: initial.sellActive[code] !== false };
      });
      return copy;
    });
    // A newly added country is quoted right away (once a comparison exists).
    const added = next.filter((code) => !countries.includes(code));
    if (added.length && Object.values(perCountry).some((s) => s.status === "success")) {
      added.forEach((code) => quoteCountry(code));
    }
  };

  // Countries with the staleness flag computed against the CURRENT inputs.
  const viewState = React.useMemo(() => Object.fromEntries(countries.map((code) => {
    const state = perCountry[code] || { status: "idle" };
    return [code, { ...state, stale: state.status === "success" && state.signature !== signature }];
  })), [countries, perCountry, signature]);

  // --- Global pricing inputs (shared defaults) ---
  const [productCost, setProductCost] = React.useState(0);
  const selectedCost = selectedVariantsCost(effectiveSelection, variants, costPrice);
  React.useEffect(() => {
    if (selectedCost > 0) setProductCost(selectedCost);
  }, [selectedCost]);
  const [priceMode, setPriceMode] = React.useState("target"); // "target" = card "Calculate Selling Price" (enter the selling price, default) | "profit" = card "Calculate Profit" (enter the desired profit)
  const [targetSalePrice, setTargetSalePrice] = React.useState("");
  const [desiredProfit, setDesiredProfit] = React.useState("");
  const [zipCode, setZipCode] = React.useState("");
  const [taxZone, setTaxZone] = React.useState(null);
  const [taxLoading, setTaxLoading] = React.useState(false);
  const taxRequest = React.useRef(0);
  const handleZipChange = async (event) => {
    const request = ++taxRequest.current;
    const zip = event.target.value;
    setZipCode(zip);
    const info = await findUsaZipArea(zip);
    if (request !== taxRequest.current) return;
    if (!info) { setTaxZone(null); setTaxLoading(false); return; }
    setTaxLoading(true);
    const data = await taxProductArea(info.state, info.zip_code).catch(() => null);
    if (request !== taxRequest.current) return;
    setTaxZone(data?.zoneTax || null);
    setTaxLoading(false);
  };
  const taxRate = taxZone ? (Number(taxZone.EstimatedCombinedRate) || 0) : DEFAULT_TAX_RATE;
  const [discountPercent, setDiscountPercent] = React.useState(0);
  const discountInvalid = Number(discountPercent) > 0 && Number(discountPercent) < 3;
  const [detailsExpanded, setDetailsExpanded] = React.useState(true);

  // Primary country = first enabled country with a usable result: its price
  // is the product's default price in Mode 1, and it feeds the legacy
  // single-country fields (Product.shippingPrice, shippingQuote).
  const usable = (code) => {
    const state = viewState[code];
    return state && state.status === "success" && !state.stale && selectedOption(state);
  };
  const primaryCode = countries.find((code) => usable(code) && viewState[code].sellActive !== false) || null;
  const primaryShipping = primaryCode ? Number(selectedOption(viewState[primaryCode]).price) || 0 : 0;

  const defaultSellingPrice = React.useMemo(() => {
    if (priceMode === "profit") {
      if (!primaryCode) return 0;
      return calculateCJPricing({
        mode: "sellingPrice",
        cjProductCost: Number(productCost) || 0,
        cjShippingCost: primaryShipping,
        shippingChargedToCustomer: shippingMode === "include" ? 0 : primaryShipping,
        discountPercent: Number(discountPercent) || 0,
        desiredProfit: Math.abs(Number(desiredProfit) || 0),
        taxRate,
      }).sellingPrice;
    }
    return Number(targetSalePrice) || 0;
  }, [priceMode, primaryCode, productCost, primaryShipping, shippingMode, discountPercent, desiredProfit, taxRate, targetSalePrice]);

  // Per-country Sales Simulation -- same engine, the country's own shipping.
  const computed = React.useMemo(() => Object.fromEntries(countries.map((code) => {
    const state = viewState[code];
    const option = selectedOption(state);
    if (!option) return [code, {}];
    return [code, computeCountryPricing({
      shippingCost: Number(option.price) || 0,
      productCost,
      shippingMode,
      discountPercent,
      taxRate,
      defaultSellingPrice,
      sellingPriceOverride: state.sellingPriceOverride,
      desiredProfit: priceMode === "profit" ? desiredProfit : undefined,
    })];
  })), [countries, viewState, productCost, shippingMode, discountPercent, taxRate, defaultSellingPrice, priceMode, desiredProfit]);

  // Country shown in the detailed breakdown table.
  const [detailCode, setDetailCode] = React.useState(null);
  const shownDetailCode = (detailCode && usable(detailCode)) ? detailCode : primaryCode;
  const pricing = shownDetailCode ? computed[shownDetailCode]?.pricing || null : null;
  const shippingCost = shownDetailCode ? Number(selectedOption(viewState[shownDetailCode])?.price) || 0 : 0;

  const anyLoading = countries.some((code) => ["loading", "waiting"].includes(viewState[code]?.status));
  const anyResult = countries.some((code) => usable(code));
  const firstError = countries.map((code) => viewState[code]?.status === "error" ? viewState[code].error : "").find(Boolean) || "";
  React.useEffect(() => {
    onStateChange && onStateChange({ loading: anyLoading, hasResult: anyResult, error: firstError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyLoading, anyResult, firstError]);

  // Report to the wizard. Every ENABLED country must have a fresh result with
  // a method; disabled countries are kept (sellActive:false) when quoted.
  const destinations = buildShippingDestinationsPayload({ countries, perCountry: viewState, quantity, computed });
  const enabledCountries = countries.filter((code) => viewState[code]?.sellActive !== false);
  const allEnabledReady = enabledCountries.length > 0 && enabledCountries.every((code) => usable(code) && computed[code]?.pricing);
  const primaryPricing = primaryCode ? computed[primaryCode]?.pricing || null : null;
  React.useEffect(() => {
    onPricingChange && onPricingChange({
      ready: Boolean(allEnabledReady && primaryPricing && !discountInvalid && !noVariantSelected && quantityValid && !anyLoading),
      // Legacy single-country view = the primary country.
      shippingCost: primaryShipping,
      cjProductCost: Number(productCost) || 0,
      shippingMode,
      selectedShipping: primaryCode ? selectedOption(viewState[primaryCode]) : null,
      priceMode,
      discountPercent: Number(discountPercent) || 0,
      discountInvalid,
      quantity: Number(quantity) || 1,
      destinationCountry: primaryCode || "",
      pricing: primaryPricing,
      // Multi-country configuration.
      destinations,
      countryResults: enabledCountries.map((code) => ({ countryCode: code, profit: computed[code]?.pricing?.profit ?? null, taxAmount: computed[code]?.pricing?.taxes ?? null, customerTotal: computed[code]?.pricing?.customerTotal ?? null, ready: Boolean(usable(code)) })),
      variantSelection: variantSelectionPayload(effectiveSelection, variants),
      selectedVariantCount: selectedIds.length,
      totalVariantCount: variants.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(destinations), allEnabledReady, primaryPricing, discountInvalid, noVariantSelected, quantityValid, anyLoading, primaryShipping, productCost, shippingMode, priceMode, discountPercent, quantity, primaryCode, JSON.stringify(effectiveSelection), variants.length]);

  const renderBreakdown = () => (
    pricing ? h("div", { className: "cjds-sim-card cjds-sim-card--results" },
          h("h4", { className: "cjds-sim-card__title", style: { justifyContent: "space-between" } },
            h("span", null, `${priceMode === "profit"
              ? t("h_cjms_result_selling_price", "Selling price result")
              : t("h_cjms_result_profit", "Profit result")} -- ${countryName(shownDetailCode)}`),
            h("button", {
              type: "button",
              className: "cjds-collapse-toggle",
              onClick: () => setDetailsExpanded((expanded) => !expanded),
              "aria-label": detailsExpanded ? t("h_cj_pricing_hide_details", "Hide details") : t("h_cj_pricing_show_details", "Show details"),
              title: detailsExpanded ? t("h_cj_pricing_hide_details", "Hide details") : t("h_cj_pricing_show_details", "Show details"),
            }, detailsExpanded ? h(MdExpandLess) : h(MdExpandMore))
          ),
          h("div", { className: "cjds-sim-result-grid" },
            h("div", { className: `cjds-sim-result ${pricing.profit >= 0 ? "cjds-sim-result--profit" : "cjds-sim-result--loss"}` },
              h("span", null, t("h_cj_shipping_profit", "Final profit")),
              h("strong", null, money(pricing.profit))
            ),
            h("div", { className: "cjds-sim-result cjds-sim-result--status" },
              h("span", null, t("h_cj_shipping_status", "Status")),
              h("strong", { className: "cjds-sim-status-badge" }, pricing.profit >= 0
                ? t("h_cj_shipping_status_profit", "Profit")
                : t("h_cj_shipping_status_loss", "Loss"))
            )
          ),
          detailsExpanded ? h("div", { className: "cjds-sim-detail-grid" },
            // Colonne gauche : "Sales details" + "Purchase details" empilées --
            // même disposition que sim-order-grid/sim-mini-card de
            // salesSimulation.jsx (PaymentVente + PaymentAchat dans la même
            // carte). Toutes les lignes viennent du même objet `pricing`
            // (calculateCJPricing) -- aucun recalcul, juste une présentation
            // détaillée en plus du résumé ci-dessus.
            h("div", { className: "cjds-sim-detail-card" },
              h("h5", null, t("h_cj_shipping_sales_details", "Sales details")),
              h("table", { className: "cjds-table" }, h("tbody", null,
                h("tr", null, h("td", null, `${quantity} ${t("h_cj_shipping_items", "item(s)")}`), h("td", null, money(pricing.sellingPrice))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_shipping", "Shipping")), h("td", null, money(pricing.shippingChargedToCustomer))),
                h("tr", { className: "cjds-sim-row-struck" }, h("td", null, t("h_cj_shipping_row_tax", "Tax")), h("td", null, money(pricing.taxes))),
                h("tr", { className: "cjds-sim-row-struck" }, h("td", null, t("h_cj_shipping_row_discount", "Discount")), h("td", null, money(pricing.sellingPrice - pricing.chargedPrice))),
                h("tr", null, h("td", null, t("h_cj_pricing_row_service_fee", "Service fee")), h("td", null, money(pricing.elakayServiceFee))),
                h("tr", null, h("td", null, t("h_cj_profit_bd_stripe", "Payment fee (Stripe, 2.9% + $0.30)")), h("td", null, money(pricing.stripeFee))),
                h("tr", null, h("td", null, t("h_cj_pricing_row_selling_fee", "Selling fee")), h("td", null, money(pricing.elakaySellingFee))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_handling", "Handling & shipping fees")), h("td", null, money(0))),
                h("tr", null, h("td", null, t("h_cj_pricing_row_referral_fee", "Referral fee")), h("td", null, money(pricing.elakayReferralFee))),
                h("tr", null, h("td", null, t("h_cj_pricing_row_closing_fee", "Closing fee")), h("td", null, money(pricing.elakayClosingFee))),
                h("tr", { className: "cjds-sim-row-total" }, h("td", null, t("h_cj_shipping_row_totalfees", "Total fees")), h("td", null, money(pricing.totalFees))),
                h("tr", { className: "cjds-sim-row-net" }, h("td", null, t("h_cj_pricing_row_net_revenue", "Net revenue")), h("td", null, money(pricing.netRevenue)))
              )),
              h("h5", null, t("h_cj_shipping_purchase_details", "Purchase details")),
              h("table", { className: "cjds-table" }, h("tbody", null,
                h("tr", null, h("td", null, `${quantity} ${t("h_cj_shipping_items", "item(s)")}`), h("td", null, money(pricing.chargedPrice))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_shipping", "Shipping")), h("td", null, money(pricing.shippingChargedToCustomer))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_tax", "Tax")), h("td", null, money(pricing.taxes))),
                h("tr", { className: "cjds-sim-row-total" }, h("td", null, t("h_cj_shipping_row_total", "Total")), h("td", null, money(pricing.customerTotal)))
              ))
            ),
            h("div", { className: "cjds-sim-detail-card" },
              h("h5", null, t("h_cj_shipping_purchase_sale_details", "Purchase & sales details")),
              h("table", { className: "cjds-table" }, h("tbody", null,
                h("tr", null, h("td", null, t("h_cj_shipping_row_beforediscount", "Price before discount")), h("td", null, money(pricing.sellingPrice))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_afterdiscount", "Price after discount")), h("td", null, money(pricing.chargedPrice))),
                h("tr", null, h("td", null, `${t("h_cj_shipping_row_discount", "Discount")}: ${pricing.discountPercent}%`), h("td", null, money(pricing.sellingPrice - pricing.chargedPrice))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_shipping", "Shipping")), h("td", null, money(pricing.shippingChargedToCustomer))),
                h("tr", null, h("td", null, t("h_cj_shipping_row_tax", "Tax")), h("td", null, money(pricing.taxes))),
                h("tr", { className: "cjds-sim-row-total" }, h("td", null, t("h_cj_shipping_row_total", "Total")), h("td", null, money(pricing.customerTotal))),
                h("tr", null, h("td", null, t("h_cj_profit_bd_stripe", "Payment fee (Stripe, 2.9% + $0.30)")), h("td", null, money(pricing.stripeFee))),
                h("tr", null, h("td", null, t("h_cj_profit_bd_elakay", "Elakay fee (6% + $0.50)")), h("td", null, money(pricing.elakayFee))),
                h("tr", { className: "cjds-sim-row-total" }, h("td", null, t("h_cj_shipping_row_totalfees", "Total fees")), h("td", null, money(pricing.totalFees))),
                h("tr", { className: "cjds-sim-row-net" }, h("td", null, t("h_cj_pricing_row_net_revenue", "Net revenue")), h("td", null, money(pricing.netRevenue))),
                h("tr", { className: "cjds-sim-row-net" }, h("td", null, t("h_cj_profit_bd_net_profit", "Final profit")), h("td", null, money(pricing.profit)))
              )),
              shippingMode === "separate" ? h("p", { className: "cjds-muted" }, t(
                "h_cj_profit_bd_separate_note",
                "Shipping ({shipping}) is billed to the customer as a separate line at exact cost -- it's not part of the selling price above and never marked up.",
                { shipping: money(shippingCost) }
              )) : null
            )
          ) : null
  
      ) : h("p", { className: "cjds-muted" }, t("h_cj_shipping_enter_price", "Enter a target sale price or a desired profit to see the full breakdown.")));

  const quotedCount = countries.filter((code) => usable(code)).length;
  return h("div", { className: "cjds-sim" },

    h("div", { className: "cjds-sim-card cjds-sim-card--intro" },
      h("p", null, t(
        "h_cjms_intro",
        "Compare the real CJ shipping cost of the same variants to up to 4 countries, then check the profit of each destination: purchase price, CJ shipping for that country, taxes, discount and every Elakay fee."
      ))
    ),

    h("div", { className: "cjds-sim-card" },
      h("h4", { className: "cjds-sim-card__title" }, t("h_cj_shipping_step1", "1. Calculate CJ shipping cost")),
      h(CJCountrySelector, { countries, onChange: handleCountriesChange, t, disabled: comparing }),
      h(CJVariantMultiSelector, {
        variants, selection: effectiveSelection, onChange: setSelection, t,
        loading: variantsLoading, error: variantsError, onRetry: loadVariants, disabled: comparing,
      }),
      h("div", { className: "cjds-row cjms-compare-bar" },
        h("div", { className: "cjds-field" },
          h("label", { htmlFor: `cjms-qty-${productId}` }, t("h_cj_shipping_quantity", "Quantity")),
          h("input", { id: `cjms-qty-${productId}`, type: "number", min: 1, max: 1000, value: quantity, onChange: (e) => setQuantity(e.target.value) }),
          !quantityValid ? h("span", { className: "cjds-field-error" }, t("h_cjms_err_quantity", "Quantity must be between 1 and 1000.")) : null
        ),
        h(Button, {
          disabled: comparing || variantsLoading || noVariantSelected || !quantityValid,
          onClick: () => compareAll({ force: quotedCount > 0 }),
        }, comparing
          ? t("h_cj_shipping_calculating", "Calculating...")
          : quotedCount > 0
            ? h(React.Fragment, null, h(MdRefresh, { "aria-hidden": true }), " ", t("h_cjms_refresh_all", "Refresh all countries"))
            : t("h_cjms_compare", "Compare shipping"))
      ),
      countries.some((code) => viewState[code]?.stale)
        ? h("p", { className: "cjms-stale-note", role: "status" }, t("h_cjms_stale_note", "Variants or quantity changed: refresh the countries marked out of date."))
        : null,
      countries.some((code) => viewState[code]?.status && viewState[code].status !== "idle")
        ? h(CJShippingComparison, { countries, perCountry: viewState, computed, t })
        : null
    ),

    countries.some((code) => viewState[code]?.status && viewState[code].status !== "idle") ? h(React.Fragment, null,
      h("div", { className: "cjds-sim-card" },
        h("h4", { className: "cjds-sim-card__title" }, t("h_cj_shipping_step2", "2. Product cost & pricing mode")),
        h("div", { className: "cjds-field" },
          h("label", null, t("h_cj_shipping_purchase_price", "Product cost (CJ, editable)")),
          h("input", { type: "number", min: 0, step: "0.01", value: productCost, onChange: (e) => setProductCost(e.target.value) }),
          h("span", { className: "cjds-muted" }, t(
            "h_cjms_purchase_price_hint",
            "Defaults to the highest CJ cost among the selected variants. Shipping is handled per country below."
          ))
        ),
        // Same selectable cards (with check mark) as the Languages Spoken
        // picker -- one choice at a time. Each card shows the input named
        // after it: "Calculate Selling Price" (default) -> the seller enters
        // the selling price; "Calculate Profit" -> the seller enters the
        // desired profit.
        h(SelectableOptionGrid, {
          className: "cjms-price-mode",
          options: [
            { value: "target", label: t("h_cjms_mode_selling_price", "Calculate Selling Price") },
            { value: "profit", label: t("h_cjms_mode_profit", "Calculate Profit") },
          ],
          selectedValues: [priceMode],
          onToggle: setPriceMode,
        })
      ),

      h("div", { className: "cjds-sim-card" },
        h("h4", { className: "cjds-sim-card__title" }, t("h_cj_shipping_step3", "3. Selling price & discount")),
        // Fixed 3-column grid (price/profit, ZIP, discount): same width and
        // same baseline for the three inputs; the long hint goes below the
        // grid so it never stretches or pushes a field.
        h("div", { className: "cjms-price-fields" },
          priceMode === "target" ? h("div", { className: "cjds-field" },
            h("label", { htmlFor: `cjms-target-${productId}` }, t("h_cjms_default_price", "Product price (default for every country)")),
            h("input", {
              id: `cjms-target-${productId}`,
              type: "number", min: 0, step: "0.01", value: targetSalePrice,
              onChange: (e) => setTargetSalePrice(e.target.value),
              placeholder: t("h_cj_shipping_sale_price_ph", "e.g. 29.99"),
            })
          ) : h("div", { className: "cjds-field" },
            h("label", { htmlFor: `cjms-profit-${productId}` }, t("h_cj_shipping_desired_profit", "Desired profit")),
            h("input", { id: `cjms-profit-${productId}`, type: "number", min: 0, step: "0.01", value: desiredProfit, onChange: (e) => setDesiredProfit(e.target.value) })
          ),
          h("div", { className: "cjds-field" },
            h("label", null, t("h_cj_shipping_zip", "Your shop ZIP / postal code")),
            h("input", { type: "text", value: zipCode, onChange: handleZipChange, placeholder: "11003" }),
            taxLoading ? h("span", { className: "cjds-muted" }, t("h_cj_shipping_tax_loading", "Looking up tax zone...")) : null
          ),
          h("div", { className: "cjds-field" },
            h("label", null, t("h_cj_shipping_discount", "Discount (%)")),
            h("input", { type: "number", min: 0, max: 90, value: discountPercent, onChange: (e) => setDiscountPercent(e.target.value) }),
            discountInvalid ? h("span", { className: "cjds-field-error" }, t(
              "h_cj_shipping_discount_min",
              "Minimum discount is 3% -- use 0 for no discount."
            )) : null
          )
        ),
        h(SimulationTaxInput, { value: taxRate, onChange: (rate) => {
          taxRequest.current += 1;
          setTaxLoading(false);
          setTaxZone({ EstimatedCombinedRate: Number(rate) || 0 });
        } }),
        priceMode === "profit" && primaryCode && defaultSellingPrice > 0 ? h("p", { className: "cjds-muted cjms-price-note" }, t(
          "h_cjms_mode1_price_note",
          "Product price {price} = the price reaching this profit in {country}. Each country below shows its own required price.",
          { price: money(defaultSellingPrice), country: countryName(primaryCode) }
        )) : null
      ),

      h("div", { className: "cjds-sim-card" },
        h("h4", { className: "cjds-sim-card__title" }, t("h_cjms_step_countries", "4. Shipping and profit by country")),
        h("div", { className: "cjms-cards" },
          ...countries.map((code) => h(CJShippingCountryCard, {
            key: code,
            code,
            state: viewState[code],
            computed: computed[code],
            priceMode,
            desiredProfit,
            defaultSellingPrice,
            busy: comparing,
            t,
            onSelectCarrier: (c, carrier) => patchCountry(c, { selectedCarrier: carrier }),
            onOverrideChange: (c, value) => patchCountry(c, { sellingPriceOverride: value }),
            onToggleActive: (c, value) => patchCountry(c, { sellActive: value }),
            onRefresh: (c) => quoteCountry(c, { force: true }),
          }))
        ),
        countries.map((code) => viewState[code]?.refreshError ? h("p", { key: code, className: "cjds-field-error", role: "alert" },
          t("h_cjms_refresh_failed", "{country}: the refresh failed ({error}). The previous result is kept.", { country: countryName(code), error: viewState[code].refreshError })) : null)
      ),

      countries.filter((code) => usable(code)).length > 1 ? h("div", { className: "cjms-detail-tabs", role: "tablist", "aria-label": t("h_cjms_detail_for", "Detailed breakdown for") },
        ...countries.filter((code) => usable(code)).map((code) => h("button", {
          key: code,
          type: "button",
          role: "tab",
          "aria-selected": code === shownDetailCode,
          className: `cjms-detail-tab${code === shownDetailCode ? " cjms-detail-tab--active" : ""}`,
          onClick: () => setDetailCode(code),
        }, countryName(code)))
      ) : null,
      renderBreakdown()
    ) : null
  );
};

module.exports = { ShippingCostCalculator, calculateCJPricing };
